import { useRef, type ReactNode } from 'react';

import type { Policyholder } from '@/shared/api/resources';
import { cn } from '@/shared/lib/cn';
import { formatCpfCnpj, formatDate, formatMoney } from '@/shared/lib/format';
import { Badge, type BadgeTone } from '@/shared/ui/Badge';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useEncaixePelaLargura } from '@/shared/ui/useEncaixePelaLargura';

import { encaixarLista } from './listaDeTomadoresLayout';
import { PENDENCIA_LIMITE_DISPONIVEL, rotuloSituacaoDeCredito } from './rotulos';

interface Acoes {
  podeBloquear: boolean;
  onVisualizar: (holder: Policyholder) => void;
  onBloquear: (holder: Policyholder) => void;
}

interface ListaDeTomadoresProps extends Acoes {
  items: Policyholder[];
  loading: boolean;
  empty: ReactNode;
  /** O clique na linha ou no cartao abre a edicao rapida do cadastro. */
  onAbrir: (holder: Policyholder) => void;
}

/** Um dado da lista. O apagado e a ausencia do dado, como "direto" ou "sem limite". */
interface Dado {
  texto: string;
  apagado?: boolean;
}

function limiteGeral(holder: Policyholder): Dado {
  return holder.credit_limit.amount > 0
    ? { texto: formatMoney(holder.credit_limit) }
    : { texto: 'sem limite', apagado: true };
}

function corretor(holder: Policyholder): Dado {
  return holder.broker_id
    ? { texto: holder.broker?.label ?? 'corretor vinculado' }
    : { texto: 'direto', apagado: true };
}

function comercial(holder: Policyholder): Dado {
  return holder.commercial_owner
    ? { texto: holder.commercial_owner }
    : { texto: 'sem responsavel', apagado: true };
}

function contato(holder: Policyholder): Dado {
  const texto = holder.email || holder.contact_name;
  return texto ? { texto } : { texto: '-', apagado: true };
}

function dataDoBloqueio(holder: Policyholder): Dado {
  return holder.blocked_at
    ? { texto: formatDate(holder.blocked_at) }
    : { texto: '-', apagado: true };
}

function ignorarCcg(holder: Policyholder): string {
  return holder.ignore_ccg_signature ? 'Sim' : 'Nao';
}

/** Documento, numero e nome fantasia, para quem nao tem coluna propria para eles. */
function identificacao(holder: Policyholder): string {
  return [formatCpfCnpj(holder.document), `#${holder.number}`, holder.trade_name]
    .filter(Boolean)
    .join(' · ');
}

/** So o que foge do comum: quase nenhum tomador tem bloqueio ou dispensa da CCG. */
function marcasDoBloqueio(holder: Policyholder): string[] {
  return [
    holder.blocked_at ? `Bloqueio ${formatDate(holder.blocked_at)}` : '',
    holder.ignore_ccg_signature ? 'Ignora CCG' : '',
  ].filter(Boolean);
}

function bloqueioECcg(holder: Policyholder): Dado {
  const marcas = marcasDoBloqueio(holder);
  return marcas.length > 0
    ? { texto: marcas.join(' · ') }
    : { texto: 'Sem bloqueio', apagado: true };
}

/** A marca vem antes do contato: o e-mail longo nao pode esconder um bloqueio. */
function bloqueioEContato(holder: Policyholder): Dado {
  const texto = [...marcasDoBloqueio(holder), holder.email || holder.contact_name]
    .filter(Boolean)
    .join(' · ');
  return texto ? { texto } : { texto: 'Sem contato', apagado: true };
}

/**
 * Texto em uma linha so: a linha da tabela nunca cresce por causa dele. O que nao cabe
 * vira reticencias, e o texto inteiro aparece ao passar o mouse.
 */
function Linha({ dado, className }: { dado: Dado; className?: string }) {
  return (
    <span
      title={dado.texto}
      className={cn('block truncate', className, dado.apagado && 'text-ink-400')}
    >
      {dado.texto}
    </span>
  );
}

function Detalhe({ dado, className }: { dado: Dado; className?: string }) {
  return (
    <Linha
      dado={dado}
      className={cn('text-2xs', !dado.apagado && 'text-ink-500', className)}
    />
  );
}

function Nome({ holder }: { holder: Policyholder }) {
  return (
    <Linha dado={{ texto: holder.legal_name }} className="font-medium text-ink-900" />
  );
}

function Disponivel({ className }: { className?: string }) {
  return (
    <span
      title={PENDENCIA_LIMITE_DISPONIVEL}
      className={cn('block truncate text-ink-400', className)}
    >
      pendente
    </span>
  );
}

function CreditoDoTomador({ holder }: { holder: Policyholder }) {
  let tom: BadgeTone = 'neutral';
  if (holder.credit_status) {
    tom = holder.credit_status === 'aprovado' ? 'success' : 'danger';
  }
  return (
    <Badge tone={tom} className="whitespace-nowrap">
      {rotuloSituacaoDeCredito(holder.credit_status)}
    </Badge>
  );
}

/** As acoes nao abrem a edicao rapida: o clique para aqui e nao chega na linha. */
function pararClique(event: { stopPropagation: () => void }) {
  event.stopPropagation();
}

function podeMostrarBloquear(holder: Policyholder, podeBloquear: boolean): boolean {
  return podeBloquear && holder.status !== 'bloqueado';
}

/**
 * As acoes da linha em icones: o olho de visualizar e o cadeado de bloquear, com o nome
 * na dica e no leitor de tela. Em todas as disposicoes, da tabela larga ao cartao.
 */
function AcoesEmIcones({
  holder,
  podeBloquear,
  onVisualizar,
  onBloquear,
}: Acoes & { holder: Policyholder }) {
  return (
    <div className="flex justify-end gap-1" onClick={pararClique}>
      <AcaoComIcone
        acao="visualizar"
        rotulo="Visualizar"
        onClick={() => onVisualizar(holder)}
      />
      {podeMostrarBloquear(holder, podeBloquear) ? (
        <AcaoComIcone
          acao="bloquear"
          rotulo="Bloquear"
          onClick={() => onBloquear(holder)}
        />
      ) : null}
    </div>
  );
}

function colunaDosLimites(): Column<Policyholder> {
  return {
    key: 'limite',
    header: 'Limite geral',
    detail: 'Limite disponivel',
    align: 'right',
    width: '9.25rem',
    render: (holder) => (
      <>
        <Linha dado={limiteGeral(holder)} className="tabular-nums" />
        <Disponivel className="text-2xs" />
      </>
    ),
  };
}

function colunaDasSituacoes(width: string): Column<Policyholder> {
  return {
    key: 'credito',
    header: 'Status',
    detail: 'Situacao',
    width,
    render: (holder) => (
      <div className="flex flex-col items-start gap-1">
        <CreditoDoTomador holder={holder} />
        <StatusBadge kind="cadastro" value={holder.status} />
      </div>
    ),
  };
}

function colunaDoCorretor(width: string): Column<Policyholder> {
  return {
    key: 'corretor',
    header: 'Corretor',
    detail: 'Comercial',
    width,
    render: (holder) => (
      <>
        <Linha dado={corretor(holder)} />
        <Detalhe dado={comercial(holder)} />
      </>
    ),
  };
}

function colunaDosIcones(acoes: Acoes): Column<Policyholder> {
  return {
    key: 'acoes',
    header: 'Acoes',
    align: 'right',
    // Dois icones de 32 px e o vao, mais o respiro da celula; um so sem a permissao.
    width: acoes.podeBloquear ? '6.5rem' : '4.25rem',
    render: (holder) => <AcoesEmIcones holder={holder} {...acoes} />,
  };
}

function colunasAmplas(acoes: Acoes): Column<Policyholder>[] {
  return [
    {
      key: 'documento',
      header: 'CPF/CNPJ',
      detail: 'Numero',
      width: '9.75rem',
      render: (holder) => (
        <>
          <Linha
            dado={{ texto: formatCpfCnpj(holder.document) }}
            className="tabular-nums"
          />
          <Detalhe dado={{ texto: String(holder.number) }} className="tabular-nums" />
        </>
      ),
    },
    {
      key: 'nome',
      header: 'Nome',
      detail: 'Nome fantasia',
      render: (holder) => (
        <>
          <Nome holder={holder} />
          {holder.trade_name ? <Detalhe dado={{ texto: holder.trade_name }} /> : null}
        </>
      ),
    },
    colunaDosLimites(),
    colunaDasSituacoes('7.5rem'),
    {
      key: 'bloqueio',
      header: 'Data bloqueio',
      detail: 'Ignorar CCG',
      width: '7.5rem',
      render: (holder) => (
        <>
          <Linha dado={dataDoBloqueio(holder)} className="tabular-nums" />
          <Detalhe dado={{ texto: ignorarCcg(holder) }} />
        </>
      ),
    },
    colunaDoCorretor('13%'),
    {
      key: 'contato',
      header: 'Contato',
      width: '13%',
      render: (holder) => <Linha dado={contato(holder)} />,
    },
    colunaDosIcones(acoes),
  ];
}

function colunasReunidas(acoes: Acoes): Column<Policyholder>[] {
  return [
    {
      key: 'nome',
      header: 'Nome',
      detail: 'CPF/CNPJ, numero e nome fantasia',
      render: (holder) => (
        <>
          <Nome holder={holder} />
          <Detalhe dado={{ texto: identificacao(holder) }} className="tabular-nums" />
        </>
      ),
    },
    colunaDosLimites(),
    colunaDasSituacoes('6.75rem'),
    colunaDoCorretor('14%'),
    {
      key: 'contato',
      header: 'Contato',
      detail: 'Bloqueio e CCG',
      width: '16%',
      render: (holder) => (
        <>
          <Linha dado={contato(holder)} />
          <Detalhe dado={bloqueioECcg(holder)} />
        </>
      ),
    },
    colunaDosIcones(acoes),
  ];
}

function colunasEstreitas(acoes: Acoes): Column<Policyholder>[] {
  return [
    {
      key: 'nome',
      header: 'Nome',
      detail: 'CPF/CNPJ, fantasia, bloqueio e contato',
      render: (holder) => (
        <>
          <Nome holder={holder} />
          <Detalhe dado={{ texto: identificacao(holder) }} className="tabular-nums" />
          <Detalhe dado={bloqueioEContato(holder)} />
        </>
      ),
    },
    colunaDosLimites(),
    colunaDasSituacoes('6.75rem'),
    colunaDoCorretor('16%'),
    colunaDosIcones(acoes),
  ];
}

const colunasDoEncaixe = {
  ampla: colunasAmplas,
  reunida: colunasReunidas,
  estreita: colunasEstreitas,
  // Os cartoes nao tem colunas: nessa largura a tabela so aparece carregando ou vazia.
  cartoes: colunasEstreitas,
};

function Campo({
  rotulo,
  largo,
  children,
}: {
  rotulo: string;
  largo?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cn('min-w-0', largo && 'col-span-2')}>
      <dt className="text-2xs font-medium text-ink-500">{rotulo}</dt>
      <dd className="text-xs text-ink-700">{children}</dd>
    </div>
  );
}

function CartaoDeTomador({
  holder,
  podeBloquear,
  onVisualizar,
  onBloquear,
  onAbrir,
}: Acoes & { holder: Policyholder; onAbrir: (holder: Policyholder) => void }) {
  return (
    <li
      onClick={() => onAbrir(holder)}
      className={cn(
        'flex min-w-0 cursor-pointer flex-col gap-3 rounded-md border border-line',
        'bg-surface p-3 shadow-(--shadow-xs) transition-[border-color,box-shadow]',
        'duration-(--duration-fast) hover:border-line-strong hover:shadow-(--shadow-md)',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p
            title={holder.legal_name}
            className="line-clamp-2 text-sm leading-snug font-medium wrap-anywhere text-ink-900"
          >
            {holder.legal_name}
          </p>
          {holder.trade_name ? (
            <Detalhe dado={{ texto: holder.trade_name }} className="mt-0.5" />
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <CreditoDoTomador holder={holder} />
          <StatusBadge kind="cadastro" value={holder.status} />
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-2 border-t border-line pt-2.5">
        <Campo rotulo="CPF/CNPJ">
          <Linha
            dado={{ texto: formatCpfCnpj(holder.document) }}
            className="tabular-nums"
          />
        </Campo>
        <Campo rotulo="Numero">
          <Linha dado={{ texto: String(holder.number) }} className="tabular-nums" />
        </Campo>
        <Campo rotulo="Limite geral">
          <Linha dado={limiteGeral(holder)} className="tabular-nums" />
        </Campo>
        <Campo rotulo="Limite disponivel">
          <Disponivel />
        </Campo>
        <Campo rotulo="Data bloqueio">
          <Linha dado={dataDoBloqueio(holder)} className="tabular-nums" />
        </Campo>
        <Campo rotulo="Ignorar CCG">{ignorarCcg(holder)}</Campo>
        <Campo rotulo="Corretor">
          <Linha dado={corretor(holder)} />
        </Campo>
        <Campo rotulo="Comercial">
          <Linha dado={comercial(holder)} />
        </Campo>
        <Campo rotulo="Contato" largo>
          <Linha dado={contato(holder)} />
        </Campo>
      </dl>

      <div className="flex justify-end">
        <AcoesEmIcones
          holder={holder}
          podeBloquear={podeBloquear}
          onVisualizar={onVisualizar}
          onBloquear={onBloquear}
        />
      </div>
    </li>
  );
}

/**
 * A lista de tomadores nas suas quatro disposicoes (`listaDeTomadoresLayout`).
 *
 * Os dados sao os mesmos em todas: CPF/CNPJ, Nome, Limite geral, Limite disponivel,
 * Status, Data bloqueio e Ignorar CCG, mais numero, comercial, corretor, situacao e
 * contato, com as acoes sempre na ultima coluna. Nenhuma disposicao rola para o lado nem
 * corta coluna: o que muda e como os dados se agrupam.
 */
export function ListaDeTomadores({
  items,
  loading,
  empty,
  onAbrir,
  ...acoes
}: ListaDeTomadoresProps) {
  const area = useRef<HTMLDivElement>(null);
  const encaixe = useEncaixePelaLargura(area, encaixarLista);

  return (
    <div ref={area} className="@container">
      {encaixe === 'cartoes' && !loading && items.length > 0 ? (
        <ul className="grid gap-2.5 bg-surface-sunken p-2.5 @min-[38rem]:grid-cols-2">
          {items.map((holder) => (
            <CartaoDeTomador
              key={holder.id}
              holder={holder}
              onAbrir={onAbrir}
              {...acoes}
            />
          ))}
        </ul>
      ) : (
        <DataTable
          fluida
          columns={colunasDoEncaixe[encaixe](acoes)}
          items={items}
          keyOf={(holder) => holder.id}
          loading={loading}
          onRowClick={onAbrir}
          empty={empty}
        />
      )}
    </div>
  );
}
